import React from "react";
import { Modal } from 'react-bootstrap';
import { DateRange } from 'react-date-range';
import * as locales from 'react-date-range/dist/locale';
import { shouldHideBooking } from "../../../utils/namings-classnames";
import UserContext from "../../../userContext";
import Loader from "../Loader";

export default function Booking({ data, house, close, onBookingCreated }) {
    const [dates, setDates] = React.useState({
        startDate: new Date(),
        endDate: new Date(),
        key: 'selection'
    });
    const [disabled, setDisabled] = React.useState([]);
    const [error, setError] = React.useState('');
    const [desc, setDesc] = React.useState('');
    const [loading, setLoading] = React.useState(false);

    const userContext = React.useContext(UserContext);

    React.useEffect(() => {
        const disabledDates = [];

        house.bookings.forEach((booking) => {
            if (booking.cancelled || shouldHideBooking(booking)) return;

            let currentDate = new Date(booking.dates.start);
            const endDate = new Date(booking.dates.end);

            while (currentDate <= endDate) {
                disabledDates.push(new Date(currentDate));
                currentDate = new Date(currentDate.setDate(currentDate.getDate() + 1));
            }
        });

        setDisabled(disabledDates);
    }, [house.bookings]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const startDate = new Date(dates.startDate);
        startDate.setHours(0, 0, 0, 0);

        const endDate = new Date(dates.endDate);
        endDate.setHours(0, 0, 0, 0);

        if (startDate.getTime() === endDate.getTime()) {
            setError('Veuillez sélectionner une période');
            return;
        }

        setLoading(true);

        try {
            const bookingPayload = {
                dates: {
                    start: dates.startDate,
                    end: dates.endDate,
                },
                description: desc.trim(),
                platform: "back"
            };

            await userContext.apiReducer('addBooking', {
                ...bookingPayload
            }, { name: house.name });

            setLoading(false);
            if (onBookingCreated) {
                onBookingCreated(bookingPayload);
            }
            close();
        } catch (err) {
            setLoading(false);
            setError('Une erreur est survenue. Veuillez réessayer.');
            console.error(err);
        }
    };

    return (
        <Modal show={data.addBooking} onHide={close} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="fas fa-calendar-plus" style={{ marginRight: '10px', color: '#faba79' }} />
                    Bloquer votre propriété
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div className="booking-modal-content">
                    {/* Property Info */}
                    <div className="booking-property-info">
                        <i className="fas fa-home" />
                        <span>{house.name}</span>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="booking-error">
                            <i className="fas fa-exclamation-circle" />
                            {error}
                        </div>
                    )}

                    {/* Date Range Picker */}
                    <div className="booking-calendar-wrapper">
                        <DateRange
                            onChange={(item) => setDates(item.selection)}
                            ranges={[dates]}
                            disabledDates={disabled}
                            locale={locales['fr']}
                            direction="vertical"
                            scroll={{ enabled: false }}
                            rangeColors={['#faba79']}
                            color="#faba79"
                            months={1}
                            minDate={new Date()}
                            showDateDisplay={true}
                            moveRangeOnFirstSelection={false}
                        />
                    </div>

                    {/* Description */}
                    <div className="booking-description">
                        <label htmlFor="booking-desc">
                            <i className="fas fa-pen" />
                            Description (optionnelle)
                        </label>
                        <textarea
                            id="booking-desc"
                            placeholder="Ex: Vacances personnelles, travaux, famille"
                            value={desc}
                            onChange={(e) => setDesc(e.target.value)}
                            rows={3}
                        />
                    </div>

                    {/* Selected Dates Summary */}
                    {dates.startDate && dates.endDate && dates.startDate.getTime() !== dates.endDate.getTime() && (
                        <div className="booking-summary">
                            <div className="summary-item">
                                <span className="summary-label">Début</span>
                                <span className="summary-value">
                                    {dates.startDate.toLocaleDateString('fr-FR', {
                                        weekday: 'short',
                                        day: 'numeric',
                                        month: 'long'
                                    })}
                                </span>
                            </div>
                            <div className="summary-arrow">
                                <i className="fas fa-arrow-right" />
                            </div>
                            <div className="summary-item">
                                <span className="summary-label">Fin</span>
                                <span className="summary-value">
                                    {dates.endDate.toLocaleDateString('fr-FR', {
                                        weekday: 'short',
                                        day: 'numeric',
                                        month: 'long'
                                    })}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </Modal.Body>

            <Modal.Footer>
                <button type="button" className="btn btn-cancel" onClick={close}>
                    Annuler
                </button>
                <button type="submit" className="btn btn-confirm" onClick={handleSubmit}>
                    <i className="fas fa-check" />
                    Confirmer le blocage
                </button>
            </Modal.Footer>

            <Loader show={loading} message="Envoi de la demande..." />

            <style>{`
                .booking-modal-content {
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                }

                .booking-property-info {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 12px 16px;
                    background: linear-gradient(135deg, #003952 0%, #002238 100%);
                    color: white;
                    border-radius: 12px;
                    font-weight: 500;
                }

                .booking-property-info i {
                    color: #faba79;
                }

                .booking-error {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 12px 16px;
                    background: rgba(248, 113, 113, 0.1);
                    color: #dc2626;
                    border-radius: 12px;
                    font-size: 0.9rem;
                    border: 1px solid rgba(248, 113, 113, 0.2);
                }

                .booking-calendar-wrapper {
                    display: flex;
                    justify-content: center;
                    background: #f8f9fa;
                    border-radius: 16px;
                    padding: 16px;
                    overflow: hidden;
                }

                .booking-calendar-wrapper .rdrCalendarWrapper {
                    width: 100%;
                    max-width: 350px;
                }

                .booking-description {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .booking-description label {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-weight: 600;
                    color: #3f3f46;
                    font-size: 0.9rem;
                }

                .booking-description label i {
                    color: #003952;
                }

                .booking-description textarea {
                    width: 100%;
                    padding: 14px 16px;
                    border: 2px solid #e4e4e7;
                    border-radius: 12px;
                    font-size: 0.95rem;
                    font-family: inherit;
                    resize: vertical;
                    transition: all 0.2s ease;
                }

                .booking-description textarea:focus {
                    outline: none;
                    border-color: #faba79;
                    box-shadow: 0 0 0 3px rgba(250, 186, 121, 0.15);
                }

                .booking-description textarea::placeholder {
                    color: #a1a1aa;
                }

                .booking-summary {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 16px;
                    padding: 16px;
                    background: #f0fdf4;
                    border-radius: 12px;
                    border: 1px solid #bbf7d0;
                }

                .summary-item {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 4px;
                }

                .summary-label {
                    font-size: 0.75rem;
                    font-weight: 600;
                    color: #059669;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                }

                .summary-value {
                    font-size: 0.9rem;
                    font-weight: 500;
                    color: #065f46;
                    text-transform: capitalize;
                }

                .summary-arrow {
                    color: #059669;
                    opacity: 0.5;
                }

                .modal-footer .btn-cancel {
                    background: #f4f4f5;
                    border: none;
                    color: #3f3f46;
                    border-radius: 12px;
                    padding: 12px 24px;
                    font-weight: 500;
                    transition: all 0.2s ease;
                }

                .modal-footer .btn-cancel:hover {
                    background: #e4e4e7;
                }

                .modal-footer .btn-confirm {
                    background: linear-gradient(135deg, #faba79 0%, #e89a4d 100%);
                    border: none;
                    color: #002238;
                    border-radius: 12px;
                    padding: 12px 24px;
                    font-weight: 600;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    transition: all 0.2s ease;
                }

                .modal-footer .btn-confirm:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(250, 186, 121, 0.4);
                }

                @media (max-width: 576px) {
                    .booking-summary {
                        flex-direction: column;
                        gap: 12px;
                    }

                    .summary-arrow {
                        transform: rotate(90deg);
                    }
                }
            `}</style>
        </Modal>
    );
}
